// Data hygiene tab — open opportunities with stale or missing fields.
// Sortable columns (SGNL-63).
// SGNL-176: Health Signals filter once lived here (sourced from global
//   filter bar's SIGNAL_DEFS). Pre-SGNL-222 the chips filtered by
//   `r.active_signals` (backend slugs) while the row ISSUES column was
//   computed independently in `computeHygieneBuckets` — so every chip
//   returned 0 matches. See lib/hygiene_filters.ts.
// SGNL-211: Export CSV — uses lib/csv for RFC-4180 quoting +
// SGNL-222: chip vocabulary now mirrors `computeHygieneBuckets` output
//   (tab-local state, multi-select OR semantics, counts on each chip).

import type { OpportunityRow } from "../types";
import { computeHygieneBuckets, type HygieneBucket, type HygieneRow } from "../lib/aggregations";
import { downloadCsv, toCsvRow } from "../lib/csv";
import { esc } from "../lib/format";
import { bindSortHeaders, makeSortable, type SortColumn, type SortState } from "../lib/sortable";
import {
  HYGIENE_FILTERS,
  countHygieneMatches,
  filterByHygieneSelection,
} from "../lib/hygiene_filters";

// SGNL-222 — row-issue strings emitted by computeHygieneBuckets use
// internal-style underscores (e.g. "missing close_date") because they
// double as predicate keys. For the human-readable badge in the
// ISSUES table cell, normalise underscores to spaces. Per
// code-reviewer MED on this PR.
function _displayIssueLabel(raw: string): string {
  return raw.replace(/_/g, " ");
}

const BUCKET_CHIP: Record<HygieneBucket, string> = {
  fresh: "chip aqua",
  stale: "chip magenta",
  old: "chip coral",
};

// Custom accessor for `issues` so users can sort by problem severity:
// number of issues per row, descending = worst hygiene first.
const COLUMNS: SortColumn<HygieneRow>[] = [
  { key: "name", label: "Opp" },
  { key: "bu", label: "BU" },
  { key: "account_name", label: "Account" },
  { key: "owner_name", label: "Owner" },
  { key: "stage", label: "Stage" },
  { key: "age_bucket", label: "Age" },
  { key: "days_since_update", label: "Days idle", numeric: true },
  { key: "issues", label: "Issues", accessor: (r) => r.issues.length },
];

let sortState: SortState<HygieneRow> = { field: "days_since_update", dir: "desc" };

// SGNL-222 — tab-local chip selection. Not in the global Filters state
// because the chip vocabulary is hygiene-specific (no meaning in Deal
// Explorer / Pipeline Analytics). Survives sort re-renders within the
// tab, resets on navigation away — same lifecycle as `sortState`.
let selectedChips: Set<string> = new Set();

/** SGNL-211 — CSV column definitions. Single source of truth for
 * both the visible table headers AND the CSV export header (CR F-02
 * / arch F-04 — header was previously a separate hardcoded string).
 * Adding / renaming / reordering a column updates both surfaces. */
const CSV_COLUMNS: ReadonlyArray<{
  label: string;
  value: (r: HygieneRow) => string | number | null;
}> = [
  { label: "Opp", value: (r) => r.name },
  { label: "BU", value: (r) => r.bu },
  { label: "Account", value: (r) => r.account_name },
  { label: "Owner", value: (r) => r.owner_name },
  { label: "Stage", value: (r) => r.stage },
  { label: "Age bucket", value: (r) => r.age_bucket },
  { label: "Days idle", value: (r) => r.days_since_update },
  // Issues joined with "; " — using ";" reads cleaner than a comma
  // (which would force quoting via csvCell either way).
  { label: "Issues", value: (r) => r.issues.join("; ") },
];

/** Build a CSV from the flagged hygiene rows.
 *
 * Column set matches the visible table via CSV_COLUMNS. Every value
 * passes through csvCell (via toCsvRow) for RFC-4180 quoting +
 * formula-injection defence. CRLF line endings per RFC-4180 §2.1.
 *
 * NOTE: exports the rows the caller passes in — the button handler
 * passes `sorted` (the post-filter, post-sort set) so the CSV mirrors
 * what the user currently sees. The footnote tells the user that the
 * export is what's flagged after filters.
 *
 * Exported for unit-test reach; production callers go through the
 * Export CSV button handler below. */
export function toHygieneCsv(rows: readonly HygieneRow[]): string {
  const header = CSV_COLUMNS.map((c) => c.label).join(",");
  const lines = rows.map((r) => toCsvRow(CSV_COLUMNS.map((c) => c.value(r))));
  return [header, ...lines].join("\r\n") + "\r\n";
}

/** Internal — render the chip filter bar. Chips with count=0 are
 * hidden so the UI only shows actionable filters for the current
 * dataset. Selected chips render with the aqua class + a leading ✓
 * (matches the legacy SIGNAL_DEFS chip styling so the visual language
 * carries over). */
function renderHygieneFilter(counts: Record<string, number>): string {
  // PR-Agent #140 (importance 7): if a selected chip's count drops to 0
  // (e.g. user selects a chip then the dataset changes), the chip would
  // disappear leaving the filter ON with no way to toggle it off. Always
  // keep selected chips visible so users can clear them, even at count=0.
  const visible = HYGIENE_FILTERS.filter(
    (f) => counts[f.slug] > 0 || selectedChips.has(f.slug),
  );
  if (visible.length === 0) return "";
  return `
    <div class="tag-filter" data-testid="hygiene-filter-bar" style="margin-top:4px;">
      <span class="muted" style="font-size:0.85em; margin-right:6px;">Health signals:</span>
      ${visible
        .map((f) => {
          const isSel = selectedChips.has(f.slug);
          const cls = isSel ? "chip aqua" : "chip";
          return `<button type="button" class="${cls}"
            data-testid="hygiene-filter-chip"
            data-hygiene-filter="${esc(f.slug)}">${isSel ? "✓ " : ""}${esc(f.label)} · ${counts[f.slug]}</button>`;
        })
        .join("")}
    </div>`;
}

export function renderHygieneTab(host: HTMLElement, rows: OpportunityRow[]): void {
  // Compute the full flagged set ONCE. Chip counts are derived from the
  // unfiltered set so each chip shows the total number of rows that
  // category WOULD match — not what's left after other selections.
  // Otherwise selecting one chip would zero-out the others, defeating
  // the OR semantics.
  const flagged = computeHygieneBuckets(rows);
  const counts = countHygieneMatches(flagged);

  // Apply chip selection BEFORE the per-bucket counters in the header,
  // so "N flagged" reflects what's actually showing.
  const filtered = filterByHygieneSelection(flagged, selectedChips);

  const bucketCounts = { fresh: 0, stale: 0, old: 0 };
  for (const f of filtered) bucketCounts[f.age_bucket]++;
  // Header counter alignment with chip vocabulary (per code-reviewer
  // HIGH on this PR): the `Stale 60d+` chip's predicate is
  // age_bucket === "stale" || age_bucket === "old", so the header's
  // "stale" count must sum BOTH buckets to match the chip badge. The
  // "old" count remains strict (only age_bucket === "old") since the
  // `Old 120d+` chip is a strict subset.
  const staleOrOlder = bucketCounts.stale + bucketCounts.old;
  // PR-Agent #140 (importance 6): rename for clarity — `sortedAll` is
  // the FULL sorted set (CSV export path); the table-render path uses
  // .slice(0, 200) inline below. makeSortable doesn't truncate
  // internally — verified against lib/sortable.ts. The rename makes the
  // CSV's "all N filtered rows" footnote demonstrably correct.
  const { sorted: sortedAll, headHtml } = makeSortable(filtered, COLUMNS, sortState);

  const totalIssues = filtered.reduce((n, r) => n + r.issues.length, 0);

  host.innerHTML = `
    <div class="tab-content" data-testid="hygiene-tab">
      <div class="tab-header">
        <h2>Data hygiene
          <span class="row-count">${filtered.length} flagged · ${bucketCounts.old} old (120d+) · ${staleOrOlder} stale (60d+) · ${totalIssues} total issues</span>
        </h2>
        <div class="tab-header-actions">
          <button type="button" class="btn-secondary"
                  data-action="hygiene-csv"
                  data-testid="hygiene-csv-export"
                  ${sortedAll.length === 0 ? "disabled" : ""}>
            Export CSV
          </button>
        </div>
      </div>
      ${renderHygieneFilter(counts)}
      ${filtered.length === 0
        ? selectedChips.size > 0
          ? `<div class="empty-bucket-card"><p>No rows match the selected health signals. Click a selected chip to clear it, or clear all selections to see all flagged rows.</p></div>`
          : `<div class="empty-bucket-card"><span style="font-size:1.5rem">✅</span><p>All opportunities are up to date — no hygiene issues found.</p></div>`
        : `<div class="table-wrap">
        <table class="data-table sortable">
          ${headHtml}
          <tbody>
            ${sortedAll
              .slice(0, 200)
              .map(
                (h) => `
              <tr>
                <td>${esc(h.name)}</td>
                <td>${esc(h.bu) || "—"}</td>
                <td>${esc(h.account_name) || "—"}</td>
                <td>${esc(h.owner_name) || "—"}</td>
                <td>${esc(h.stage) || "—"}</td>
                <td><span class="${BUCKET_CHIP[h.age_bucket]}">${h.age_bucket}</span></td>
                <td class="num">${h.days_since_update?.toLocaleString() ?? "—"}</td>
                <td>${h.issues.map((i) => `<span class="chip">${esc(_displayIssueLabel(i))}</span>`).join(" ")}</td>
              </tr>`,
              )
              .join("")}
          </tbody>
        </table>
      </div>
      ${filtered.length > 200 ? `<p class="table-footnote">Showing first 200 of ${filtered.length.toLocaleString()} flagged opps. CSV export includes all ${filtered.length.toLocaleString()} filtered rows (not just the 200 shown).</p>` : ""}
      </div>`}
    </div>
  `;

  // Chip click → toggle selection in the tab-local Set, then re-render.
  // Event delegation on the host because the chip bar can render zero
  // chips when the dataset has no hygiene issues at all.
  host
    .querySelector('[data-testid="hygiene-filter-bar"]')
    ?.addEventListener("click", (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const chip = t.closest('[data-hygiene-filter]');
      if (!(chip instanceof HTMLElement)) return;
      const slug = chip.dataset.hygieneFilter;
      if (!slug) return;
      // Explicit allowlist (per security-NIT on this PR). The slug
      // comes from a chip rendered by our own code, so this is
      // belt-and-braces against a browser extension / injected
      // content mutating data-hygiene-filter to an arbitrary value.
      if (!HYGIENE_FILTERS.some((f) => f.slug === slug)) return;
      if (selectedChips.has(slug)) selectedChips.delete(slug);
      else selectedChips.add(slug);
      renderHygieneTab(host, rows);
    });

  bindSortHeaders(host, COLUMNS, sortState, (next) => {
    sortState = next;
    renderHygieneTab(host, rows);
  });

  // SGNL-211 — Export CSV. Honours the current sort + chip selection
  // so "filter, sort, then export" is the contract. The footnote
  // discloses that the export matches what's visible after filtering.
  host
    .querySelector<HTMLButtonElement>('[data-action="hygiene-csv"]')
    ?.addEventListener("click", () => {
      if (sortedAll.length === 0) return; // button disabled in this state, but defensive
      downloadCsv(
        `signal-data-hygiene-${new Date().toISOString().slice(0, 10)}.csv`,
        toHygieneCsv(sortedAll),
      );
    });
}

/** Test-only — reset module-level chip selection between tests so
 * cross-test pollution doesn't surface as "previous test's chip
 * still selected". Not exported for production callers; vitest
 * imports it via the module path. */
export function _resetHygieneChipsForTest(): void {
  selectedChips = new Set();
}
